import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent, merge, Subscription, switchMap, throttleTime, timer } from 'rxjs';

import { ACTIVITY_THROTTLE } from '@/app/core/services/inactivity-detection/inactivity-timeouts.constants';
import { DialogHelperService } from '@/app/shared/ui-common/dialog/dialog-helper/dialog-helper.service';
import { UserContextState } from '@/app/shared/util-user/user-context._state';
import { environment } from '@/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InactivityDetectionService {
  private readonly dialogHelper = inject(DialogHelperService);
  private readonly userContextState = inject(UserContextState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inactivityTimeoutInMilliseconds =
    environment.inactivityDetection.inactivityTimeoutInMinutes * 60 * 1000;
  private readonly logoutTimeoutInMilliseconds =
    environment.inactivityDetection.logoutTimeoutInMinutes * 60 * 1000;

  private inactivityTimeoutSubscription: Subscription;

  startTracking(): void {
    const activity$ = merge(
      fromEvent(document, 'keydown'),
      fromEvent(document, 'pointerdown'),
      fromEvent(document, 'pointermove'),
      fromEvent(document, 'wheel'),
    ).pipe(
      throttleTime(ACTIVITY_THROTTLE),
      switchMap(() => timer(this.inactivityTimeoutInMilliseconds)),
      takeUntilDestroyed(this.destroyRef),
    );

    this.inactivityTimeoutSubscription = activity$.subscribe(() => this.openDialog());
  }

  private stopTracking(): void {
    this.inactivityTimeoutSubscription?.unsubscribe();
  }

  private openDialog(): void {
    this.stopTracking();

    const logoutTimerSubscription = timer(this.logoutTimeoutInMilliseconds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.userContextState.logout());

    this.dialogHelper
      .open({
        title: 'dialogs.inactivityDetection.title',
        description: 'dialogs.inactivityDetection.description',
        confirmButton: 'dialogs.inactivityDetection.confirmButton',
        cancelButton: 'dialogs.inactivityDetection.cancelButton',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        logoutTimerSubscription.unsubscribe();

        if (result) {
          this.startTracking();
          return;
        }

        this.userContextState.logout();
      });
  }
}
